/**
 * BacklinksPreviewWidget — toont op /prijzen welke directories een klant
 * deze maand zou krijgen op basis van gekozen branche. Sales-anker.
 *
 * Endpoint: GET /api/public/backlinks/preview?branche=...  (geen auth)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link2, Award, Target, RefreshCw, TrendingUp } from 'lucide-react';

const BRANCHES = [
  { id: 'bouw', label: 'Bouw / Aannemer', emoji: '🔨' },
  { id: 'horeca', label: 'Horeca / Restaurant', emoji: '🍽️' },
  { id: 'zorg', label: 'Zorg / Wellness', emoji: '💆' },
  { id: 'retail', label: 'Webshop / Retail', emoji: '🛒' },
  { id: 'automotive', label: 'Garage / Auto', emoji: '🚗' },
  { id: 'dienstverlening', label: 'Dienstverlening', emoji: '💼' },
  { id: 'onderwijs', label: 'Training / Onderwijs', emoji: '🎓' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DirectoryCard({ d }) {
  const isBranche = d.category === 'branche';
  return (
    <div data-testid={`preview-dir-${d.domain}`} style={{
      background: 'white', borderRadius: 10, padding: '12px 14px',
      border: `1px solid ${isBranche ? '#fcd34d' : '#e2e8f0'}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{d.name}</p>
        <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
          {d.domain} · {isBranche ? 'branche-specifiek' : 'algemene directory'}
        </p>
      </div>
      <div style={{
        background: d.est_da >= 70 ? '#dcfce7' : d.est_da >= 40 ? '#fef3c7' : '#f1f5f9',
        color: d.est_da >= 70 ? '#15803d' : d.est_da >= 40 ? '#a16207' : '#64748b',
        borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 800,
        whiteSpace: 'nowrap',
      }}>
        DA {d.est_da}
      </div>
    </div>
  );
}

export default function BacklinksPreviewWidget() {
  const [branche, setBranche] = useState('bouw');
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/public/backlinks/preview?branche=${branche}`)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, [branche]);

  const loading = data === null;

  const selectBranche = (id) => {
    setData(null);
    setBranche(id);
  };

  return (
    <section data-testid="backlinks-preview-widget" style={{
      maxWidth: 1100, margin: '0 auto', padding: '60px 24px',
    }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
          <Link2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
          Backlinks die ú krijgt
        </p>
        <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 8 }}>
          {data?.total_catalog_size || 80}+ directories in onze catalog
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', maxWidth: 620, margin: '0 auto', lineHeight: 1.55 }}>
          Geen vage belofte. Kies uw branche en zie exact welke hoog-DA backlinks onze AI deze maand voor u zou claimen.
        </p>
      </div>

      {/* Branche selector */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
        {BRANCHES.map((b) => (
          <button
            key={b.id}
            data-testid={`branche-${b.id}`}
            onClick={() => selectBranche(b.id)}
            style={{
              background: branche === b.id ? '#0f172a' : 'white',
              color: branche === b.id ? 'white' : '#475569',
              border: `1px solid ${branche === b.id ? '#0f172a' : '#e2e8f0'}`,
              borderRadius: 999, padding: '8px 16px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            <span>{b.emoji}</span> {b.label}
          </button>
        ))}
      </div>

      {/* Preview grid */}
      <div style={{
        background: '#f8fafc', borderRadius: 16, padding: 24, border: '1px solid #f1f5f9',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
            <Award size={16} style={{ display: 'inline', color: '#D4A017', marginRight: 6, verticalAlign: 'middle' }} />
            Top {data?.preview_count || 8} backlinks voor {BRANCHES.find((b) => b.id === branche)?.label}
          </h3>
          {loading && <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', color: '#94a3b8' }} />}
        </div>

        {data ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
            {data.directories.map((d, i) => <DirectoryCard key={i} d={d} />)}
          </div>
        ) : (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>Laden…</p>
        )}

        {data && (
          <div style={{ marginTop: 16, padding: 14, background: 'rgba(212,160,23,0.08)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <TrendingUp size={18} style={{ color: '#D4A017', flexShrink: 0 }} />
            <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
              <strong style={{ color: '#0f172a' }}>+ 17 andere directories</strong> uit onze catalog — u krijgt deze maand
              <strong style={{ color: '#0f172a' }}> 25-40 backlinks</strong>, geprioriteerd op branche-relevantie en domain authority.
              <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                Beschikbaar in <strong>Managed AI Bot</strong> (€995,95 excl. btw eerste maand).
              </span>
            </p>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </section>
  );
}
